import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { OverlayPortal } from '../../../common/overlays/OverlayFoundation'
import { BoardChartLegend, BoardChartTooltip, BoardTrendPlot } from '../../../boardui/TrendPlot'
import { formatBucket } from '../../formatters'
import { focusLine, lineState, nearestIndex, nearestLine, tooltipPosition, tooltipRows } from './chartHover'
import { curvePath } from './curve'
import { PREVIOUS_PERIOD_LABEL } from './legend'

// The plot's size, and the axis type size it resolves to: the axis labels
// grow with the widget, so the room kept for them has to grow too.
function useSize(ref, fallback) {
  const [size, setSize] = useState(fallback)
  useLayoutEffect(() => {
    const node = ref.current
    if (!node) return undefined
    const measure = () => {
      const rect = node.getBoundingClientRect()
      const fontSize = Number.parseFloat(window.getComputedStyle(node).fontSize) || fallback.fontSize
      if (rect.width && rect.height) {
        setSize((current) => (
          current.width === rect.width && current.height === rect.height && current.fontSize === fontSize
            ? current
            : { width: rect.width, height: rect.height, fontSize }
        ))
      }
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [ref, fallback.fontSize])
  return size
}

const NICE_STEPS = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10]

// A missing value (a share with no denominator that day) leaves a gap in the
// line rather than drawing it at zero.
function knownRuns(points) {
  const runs = [[]]
  for (const point of points) {
    if (point.y == null) {
      if (runs.at(-1).length) runs.push([])
    } else {
      runs.at(-1).push(point)
    }
  }
  return runs.filter((run) => run.length > 1)
}

function gappedPath(points) {
  return knownRuns(points).map(curvePath).join(' ')
}

// Close each observed run to zero, including a signed domain's interior zero
// line. Never bridge a missing observation or shade a loss to the plot bottom.
function gappedAreaPath(points, zero) {
  return knownRuns(points).map((run) => {
    const first = run[0]
    const last = run.at(-1)
    return `${curvePath(run)} L${last.x.toFixed(1)},${zero.toFixed(1)} L${first.x.toFixed(1)},${zero.toFixed(1)} Z`
  }).join(' ')
}

// A value with no known neighbour has no segment to sit on, so it is drawn
// as a dot rather than disappearing between two gaps.
function isolatedPoints(points) {
  return points.filter((point, index) => point.y != null && points[index - 1]?.y == null && points[index + 1]?.y == null)
}

// The axis top for `value`. With `even`, a whole series (counts) takes an
// even top, so the middle tick at half of it is a whole number too: a top of
// 3 would put a "2" (1.5 rounded) halfway up.
function niceMax(value, { even = false } = {}) {
  if (!value) return 1
  const magnitude = 10 ** Math.floor(Math.log10(value))
  const normalized = value / magnitude
  const step = NICE_STEPS.find((candidate) => normalized <= candidate && (!even || (candidate * magnitude) % 2 === 0)) ?? 10
  return step * magnitude
}

const DEFAULT_SIZE = Object.freeze({ width: 600, height: 220, fontSize: 11 })

// Line chart with an optional dashed previous-period series, hover
// crosshair and tooltip. Rows are { date, value } and previous rows are
// aligned by index (same number of days, earlier range). `series` draws
// several lines over the same dates instead (creators or states side by
// side): each is { key, label, rows, tone, dashed, of } with rows aligned by
// index to `rows`, which then only sets the dates; tone picks the colour
// ('1' to '4', or 'total'), a dashed line is a previous period and `of`
// names the line it is the previous period of. The line nearest the pointer
// is drawn thicker and the others step back; `highlight` does the same for
// a line picked elsewhere (its legend entry). The tooltip follows the
// pointer and is portaled, since cards and the expanded chart clip.
export function TrendChart({ rows, previous = [], series = null, format = (value) => String(value), label, previousLabel = PREVIOUS_PERIOD_LABEL, highlight = null }) {
  const ref = useRef(null)
  const tooltipRef = useRef(null)
  const { width, height, fontSize } = useSize(ref, DEFAULT_SIZE)
  const [hover, setHover] = useState(null)
  const [tooltipSize, setTooltipSize] = useState(null)
  // Room for the axis labels at the size they are drawn: a bigger widget
  // draws bigger labels, and they must never be clipped.
  const padding = { top: 8, right: 8, bottom: Math.max(22, Math.round(fontSize * 2)), left: Math.max(40, Math.round(fontSize * 3.2)) }
  const plotWidth = Math.max(10, width - padding.left - padding.right)
  const plotHeight = Math.max(10, height - padding.top - padding.bottom)

  const model = useMemo(() => {
    const values = [...rows, ...previous, ...(series || []).flatMap((item) => item.rows)].map((row) => row.value).filter((value) => value != null)
    // The domain is signed: settled GGR goes below zero when payouts exceed
    // stake, and a chart that clamped at zero would hide the size of a loss.
    // Whole values (counts) get whole ticks; an all-zero one reads 0 and 1.
    const whole = values.every(Number.isInteger)
    const lowest = Math.min(0, ...values)
    const top = niceMax(Math.max(0, ...values), { even: whole && lowest >= 0 })
    const bottom = lowest < 0 ? -niceMax(-lowest) : 0
    const span = top - bottom
    const xFor = (index, count) => padding.left + (count <= 1 ? plotWidth / 2 : (index / (count - 1)) * plotWidth)
    const yFor = (value) => (value == null ? null : padding.top + plotHeight - ((value - bottom) / span) * plotHeight)
    const points = rows.map((row, index) => ({ x: xFor(index, rows.length), y: yFor(row.value), row }))
    const previousPoints = previous.map((row, index) => ({ x: xFor(index, previous.length), y: yFor(row.value), row }))
    const lines = (series || []).map((item) => {
      // On the shared dates: a previous period a day shorter ends a day
      // early rather than being stretched across.
      const itemPoints = item.rows.map((row, index) => ({ x: xFor(index, rows.length || item.rows.length), y: yFor(row.value), row }))
      return { ...item, points: itemPoints, line: gappedPath(itemPoints), dots: isolatedPoints(itemPoints) }
    })
    // One hover path for both kinds of chart: a single line is 'current',
    // its previous period 'previous'.
    const hoverLines = lines.length ? lines : [
      { key: 'current', label, tone: '1', dashed: false, points },
      ...(previousPoints.length ? [{ key: 'previous', label: previousLabel, tone: 'previous', dashed: true, of: 'current', points: previousPoints }] : []),
    ]
    const tickValues = bottom < 0 ? [bottom, 0, top] : whole && top === 1 ? [0, 1] : [0, top / 2, top]
    const ticks = tickValues.map((value) => ({ value, y: yFor(value), baseline: bottom < 0 && value === 0 }))
    const labelEvery = Math.max(1, Math.ceil(rows.length / Math.max(2, Math.floor(plotWidth / 70))))
    const labelled = new Set()
    for (let index = rows.length - 1; index >= 0; index -= labelEvery) labelled.add(index)
    // Fill the total or a sole current line, ignoring previous periods.
    const currentLines = hoverLines.filter((item) => !item.dashed)
    const filledLine = currentLines.find((item) => item.tone === 'total')
      ?? (currentLines.length === 1 ? currentLines[0] : null)
    const area = filledLine
      ? { key: filledLine.key, tone: filledLine.tone, path: gappedAreaPath(filledLine.points, yFor(0)) }
      : null
    return {
      points, previousPoints, lines, hoverLines, ticks, labelled, area,
      line: gappedPath(points), previousLine: gappedPath(previousPoints),
      dots: isolatedPoints(points), previousDots: isolatedPoints(previousPoints),
    }
  }, [rows, previous, series, label, previousLabel, plotWidth, plotHeight, padding.left, padding.top])

  const onMove = (event) => {
    if (!model.points.length) return
    const rect = event.currentTarget.getBoundingClientRect()
    // The viewBox is the plot's measured size; a box with no layout (0 x 0)
    // is taken as unscaled.
    const scaleX = rect.width ? width / rect.width : 1
    const scaleY = rect.height ? height / rect.height : 1
    const x = (event.clientX - rect.left) * scaleX
    const y = (event.clientY - rect.top) * scaleY
    const index = nearestIndex(model.points, x)
    const key = nearestLine(model.hoverLines, index, y)
    setHover({ index, key, clientX: event.clientX, clientY: event.clientY })
  }

  // A hover past the last bucket (the rows shrank under the pointer: a
  // coarser grain, a refetch) points at nothing and is dropped.
  const current = hover && hover.index < model.points.length ? hover : null

  // The tooltip is fixed to the viewport: a scroll or a resize would leave
  // it behind, so either one ends the hover.
  const hovering = current != null
  useEffect(() => {
    if (!hovering) return undefined
    const clear = () => setHover(null)
    window.addEventListener('scroll', clear, true)
    window.addEventListener('resize', clear)
    return () => {
      window.removeEventListener('scroll', clear, true)
      window.removeEventListener('resize', clear)
    }
  }, [hovering])

  // Measured before paint, so the tooltip is placed by its own size and is
  // never seen in the corner first. Measured again when its content changes,
  // not only the pointer: rows that arrive while the pointer rests resize it.
  useLayoutEffect(() => {
    const node = tooltipRef.current
    if (!current || !node) {
      setTooltipSize(null)
      return
    }
    const rect = node.getBoundingClientRect()
    setTooltipSize((size) => (size && size.width === rect.width && size.height === rect.height ? size : { width: rect.width, height: rect.height }))
  }, [current, model, highlight, format])

  const index = current?.index
  const active = current == null ? null : model.points[index]
  const activePrevious = active ? model.previousPoints[index] : null
  // The hovered (or highlighted) line names its row in the tooltip; the
  // pair it belongs to, a line and its previous period, comes forward.
  const rawFocus = current?.key ?? highlight
  const focusKey = focusLine(model.hoverLines, rawFocus)
  // The pair in focus is drawn last, over the others, its previous period
  // under its line.
  const drawnLines = model.lines
    .map((item, order) => ({ item, order, front: lineState(item, focusKey) === 'active' ? (item.dashed ? 1 : 2) : 0 }))
    .sort((left, right) => left.front - right.front || left.order - right.order)
    .map(({ item }) => item)
  const line = (item, path, previous = false) => ({
    key: item.key,
    tone: item.tone,
    dashed: item.dashed,
    previous,
    path,
    state: lineState(item, focusKey),
  })
  const dot = (point, key, item, radius, { previous = false, halo = false } = {}) => ({
    key,
    x: point.x,
    y: point.y,
    tone: item.tone,
    dashed: item.dashed,
    previous,
    r: radius,
    state: lineState(item, focusKey),
    halo,
  })
  const currentItem = { key: 'current', tone: '1' }
  const previousItem = { key: 'previous', tone: 'previous', of: 'current', dashed: true }
  const plotLines = [
    ...(model.previousPoints.length > 1 ? [line(previousItem, model.previousLine, true)] : []),
    ...(model.lines.length
      ? drawnLines.filter((item) => item.points.length > 1).map((item) => line(item, item.line))
      : model.points.length > 1 ? [line(currentItem, model.line)] : []),
  ]
  const dots = [
    ...model.previousDots.map((point, order) => dot(point, `previous-${order}`, previousItem, 2.5, { previous: true })),
    ...(model.lines.length
      ? drawnLines.flatMap((item) => item.dots.map((point, order) => dot(point, `${item.key}-${order}`, item, item.dashed ? 2 : 2.5)))
      : model.dots.map((point, order) => dot(point, `current-${order}`, currentItem, 2.5))),
  ]
  const markers = active ? [
    ...(activePrevious?.y != null ? [dot(activePrevious, 'previous', previousItem, 4, { previous: true })] : []),
    ...(model.lines.length
      ? drawnLines.filter((item) => item.points[index]?.y != null).map((item) => dot(item.points[index], item.key, item, item.dashed ? 3 : lineState(item, focusKey) === 'active' ? 5 : 4, { halo: !item.dashed && lineState(item, focusKey) === 'active' }))
      : active.y != null ? [dot(active, 'current', currentItem, lineState(currentItem, focusKey) === 'active' ? 5 : 4, { halo: lineState(currentItem, focusKey) === 'active' })] : []),
  ] : []
  const tooltipLines = active ? tooltipRows(model.hoverLines, index, rawFocus) : []
  const position = active && tooltipSize
    ? tooltipPosition({ x: current.clientX, y: current.clientY }, tooltipSize, { width: window.innerWidth, height: window.innerHeight })
    : null

  return (
    <>
      <BoardTrendPlot
        containerRef={ref}
        width={width}
        height={height}
        padding={padding}
        label={label}
        ticks={model.ticks.map((tick) => ({ key: tick.value, ...tick, label: format(tick.value, { axis: true }) }))}
        labels={model.points.flatMap((point, pointIndex) => model.labelled.has(pointIndex) ? [{ key: point.row.date, x: point.x, label: formatBucket(point.row.date), align: pointIndex === 0 ? 'start' : pointIndex === model.points.length - 1 ? 'end' : 'middle' }] : [])}
        lines={plotLines}
        dots={dots}
        area={model.area ? { ...model.area, state: lineState(model.area, focusKey) } : null}
        cursor={active ? { x: active.x, top: padding.top, bottom: padding.top + plotHeight } : null}
        markers={markers}
        onMove={onMove}
        onLeave={() => setHover(null)}
      />
      {active ? (
        <OverlayPortal>
          <BoardChartTooltip
            tooltipRef={tooltipRef}
            title={formatBucket(active.row.date, { long: true })}
            rows={tooltipLines.map((row) => ({ ...row, value: row.value == null ? '—' : format(row.value) }))}
            position={position}
          />
        </OverlayPortal>
      ) : null}
    </>
  )
}

// The chart's key. An entry with a `key` names a line: given `onHighlight`,
// hovering it brings that line forward, its previous period with it.
export function ChartLegend({ items, highlight = null, onHighlight = null }) {
  // A highlighted entry that left the legend (the ranking changed under the
  // pointer) never sees its mouseleave; it highlights nothing.
  const focus = items.some((item) => item.key != null && item.key === highlight) ? highlight : null
  return (
    <BoardChartLegend items={items.map((item) => {
      const hoverable = Boolean(onHighlight) && item.key != null
      return {
        ...item,
        state: hoverable ? lineState(item, focus) : null,
        onEnter: hoverable ? () => onHighlight(item.key) : undefined,
        onLeave: hoverable ? () => onHighlight(null) : undefined,
      }
    })} />
  )
}
